/**
 * Thin, defensive wrapper around Fully Kiosk Browser's `window.fully` bridge.
 *
 * Two things make a naive integration fragile, and this module exists to absorb
 * both:
 *
 * 1. `window.fully` only exists inside Fully Kiosk *with a PLUS licence and the
 *    JavaScript interface enabled*. In a desktop dev browser it is undefined.
 *    Every call here degrades to a no-op / fallback value instead of throwing.
 *
 * 2. `fully.bind(event, code)` takes a **string of JavaScript**, not a function.
 *    So we register one global dispatcher and bind a string that calls into it.
 *
 * Method names follow the Fully JS API. They are looked up dynamically, so a
 * name that is wrong or missing on an older Fully build silently no-ops rather
 * than taking the kiosk down. Verify against https://www.fully-kiosk.com/#!docs
 * when you rely on a new one.
 */

const DISPATCHER = '__kioskFullyEmit'

type AnyFn = (...args: unknown[]) => unknown

function bridge(): Record<string, unknown> | undefined {
  const w = window as unknown as { fully?: Record<string, unknown> }
  return w.fully
}

/** True when running inside Fully Kiosk with the JS interface available. */
export function isFully(): boolean {
  return bridge() !== undefined
}

function call<T>(method: string, args: unknown[], fallback: T): T {
  const f = bridge()
  const fn = f?.[method]
  if (typeof fn !== 'function') return fallback
  try {
    return (fn as AnyFn).apply(f, args) as T
  } catch (err) {
    console.warn(`[fully] ${method}() threw`, err)
    return fallback
  }
}

/* ------------------------------------------------------------------ device */

export interface DeviceInfo {
  inFully: boolean
  fullyVersion: string
  androidVersion: string
  webviewVersion: string
  deviceModel: string
  serial: string
  ip: string
  displayWidth: number
  displayHeight: number
  batteryLevel: number
  userAgent: string
  viewport: string
  devicePixelRatio: number
}

export function deviceInfo(): DeviceInfo {
  return {
    inFully: isFully(),
    fullyVersion: call('getFullyVersion', [], '-'),
    androidVersion: call('getAndroidVersion', [], '-'),
    webviewVersion: call('getWebviewVersion', [], '-'),
    deviceModel: call('getDeviceModel', [], '-'),
    serial: call('getSerialNumber', [], '-'),
    ip: call('getIp4Address', [], '-'),
    displayWidth: call('getDisplayWidth', [], -1),
    displayHeight: call('getDisplayHeight', [], -1),
    batteryLevel: call('getBatteryLevel', [], -1),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio,
  }
}

/* ------------------------------------------------------------------ actions */

export const screenOn = (): void => void call('turnScreenOn', [], undefined)
export const screenOff = (): void => void call('turnScreenOff', [], undefined)
export const startScreensaver = (): void => void call('startScreensaver', [], undefined)
export const stopScreensaver = (): void => void call('stopScreensaver', [], undefined)
export const setBrightness = (level: number): void =>
  void call('setScreenBrightness', [Math.max(0, Math.min(255, Math.round(level)))], undefined)
export const playSound = (url: string, loop = false): void =>
  void call('playSound', [url, loop], undefined)
export const showToast = (text: string): void => void call('showToast', [text], undefined)
export const clearCache = (): void => void call('clearCache', [], undefined)
export const loadUrl = (url: string): void => void call('loadUrl', [url], undefined)

/* ------------------------------------------------------------------- events */

/**
 * Fully event names we use. The full list is in the Fully docs under
 * "JavaScript Interface / bind".
 */
export type FullyEvent =
  | 'onScreensaverStart'
  | 'onScreensaverStop'
  | 'screenOn'
  | 'screenOff'
  | 'networkDisconnect'
  | 'networkReconnect'
  | 'internetDisconnect'
  | 'internetReconnect'
  | 'onMotion'
  | 'onIdle'

const listeners = new Map<string, Set<() => void>>()

function ensureDispatcher(): void {
  const w = window as unknown as Record<string, unknown>
  if (typeof w[DISPATCHER] === 'function') return
  w[DISPATCHER] = (event: string): void => {
    const set = listeners.get(event)
    if (!set) return
    for (const handler of set) {
      try {
        handler()
      } catch (err) {
        console.error(`[fully] listener for "${event}" threw`, err)
      }
    }
  }
}

/** Subscribe to a Fully event. Returns an unsubscribe function. */
export function on(event: FullyEvent, handler: () => void): () => void {
  ensureDispatcher()

  let set = listeners.get(event)
  if (!set) {
    set = new Set()
    listeners.set(event, set)
    // Bind once per event. Note the argument is a *code string*, not a function.
    call('bind', [event, `window.${DISPATCHER}(${JSON.stringify(event)})`], undefined)
  }
  set.add(handler)

  return () => {
    set.delete(handler)
  }
}
